import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '../utils/cn';

interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  icon?: ReactNode;
  label: string;
  description?: string;
  layout?: 'horizontal' | 'vertical';
  /**
   * Optional pill rendered as a ribbon sitting on the chip's top border
   * (e.g. <Badge variant="best">Best</Badge>). Absolute-positioned and
   * centered horizontally so it visually "hangs" from the card edge rather
   * than consuming a row inside the text column. Callers MUST leave at
   * least ~12px of headroom above the chip (e.g. `mt-3` on the grid) so the
   * ribbon doesn't collide with the previous element — and, when used inside
   * the accordion body, so it isn't clipped by the collapse animation's
   * `overflow-hidden` wrapper.
   */
  badge?: ReactNode;
}

/**
 * Selectable chip — used for intent / funding / payout pickers. Vertical
 * layout (icon on top of label) is the default; horizontal puts the icon
 * to the left.
 */
export const Chip = ({
  selected,
  icon,
  label,
  description,
  layout = 'vertical',
  badge,
  className,
  ...props
}: ChipProps) => {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      className={cn(
        'group relative flex items-center rounded-2xl border p-4 text-left transition-all',
        'focus-visible:ring-accent focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-bg focus-visible:outline-none',
        'active:scale-[0.98]',
        // Selected keeps the mode's surface and marks the choice with the
        // partner accent (border + 1px ring for weight). Non-selected hover
        // tints the surface in addition to the border.
        selected
          ? 'border-accent bg-bg-elevated text-fg ring-1 ring-accent'
          : 'border-border bg-bg-elevated text-fg hover:border-fg-muted hover:bg-bg-muted/60',
        layout === 'vertical' ? 'flex-col gap-2' : 'justify-start gap-3',
        className,
      )}
      {...props}
    >
      {badge && (
        // Ribbon-style placement: the badge straddles the top border line of
        // the chip ("centro del badge colineal con el borde"). We anchor
        // `top-0` to the chip's top edge and then shift the badge UP by 50%
        // of *its own* rendered height via `-translate-y-1/2` — this keeps
        // the badge's vertical center exactly on the border regardless of
        // whether the label has an icon, two lines, or a different font
        // size. Same trick on the horizontal axis (`left-1/2` +
        // `-translate-x-1/2`) centers it horizontally without measuring.
        // `z-10` keeps it above the chip's own background — important when
        // the chip is selected and its `bg-accent/10` would otherwise tint
        // the ribbon's edges. `pointer-events-none` is defensive: the
        // ribbon is decorative and must never absorb clicks intended for
        // the parent button.
        <span className="pointer-events-none absolute left-1/2 top-[-2px] z-10 -translate-x-1/2 -translate-y-1/2">
          {badge}
        </span>
      )}
      {icon && (
        <span
          className={cn(
            'flex shrink-0 items-center justify-center',
            layout === 'vertical' ? '' : 'h-10 w-10',
          )}
        >
          {icon}
        </span>
      )}
      <span className={cn('flex flex-col', layout === 'vertical' && 'items-start')}>
        <span className="text-sm font-semibold leading-tight">{label}</span>
        {description && (
          <span className="mt-0.5 text-xs leading-tight text-fg-muted">
            {description}
          </span>
        )}
      </span>
    </button>
  );
};
